import React from 'react';
import { Box, List, ListItemIcon, ListItemText, Typography, styled, Drawer, Divider, Badge, ListItemButton, Tooltip, IconButton } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import MenuOpen from '@mui/icons-material/MenuOpen';
import Menu from '@mui/icons-material/Menu';
import ListAltIcon from '@mui/icons-material/ListAlt';
import BarChartIcon from '@mui/icons-material/BarChart';
import SettingsIcon from '@mui/icons-material/Settings';
import GroupIcon from '@mui/icons-material/Group';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import InventoryIcon from '@mui/icons-material/Inventory';
import AddIcon from '@mui/icons-material/Add';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import WorkIcon from '@mui/icons-material/Work';
import UpdateIcon from '@mui/icons-material/Update';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// New icons for enhanced functionality
import DashboardIcon from '@mui/icons-material/Dashboard';
import TimelineIcon from '@mui/icons-material/Timeline';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SecurityIcon from '@mui/icons-material/Security';
import QualityIcon from '@mui/icons-material/VerifiedUser';
import BusinessIcon from '@mui/icons-material/Business';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import logoImage from '../../assets/logo.png';
import { useAuth } from '../../context/AuthContext';

const DRAWER_WIDTH = 280;
const COLLAPSED_WIDTH = 72;

const LogoContainer = styled(Box, { shouldForwardProp: (prop) => prop !== 'collapsed' })(({ theme, collapsed }) => ({
  padding: theme.spacing(collapsed ? 2 : 2, collapsed ? 1.5 : 2, collapsed ? 1.5 : 1.5, collapsed ? 1.5 : 2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: collapsed ? 'center' : 'flex-start',
  gap: theme.spacing(collapsed ? 0 : 1.5),
  height: 72,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
}));

const NavItem = styled(ListItemButton, { shouldForwardProp: (prop) => prop !== 'active' && prop !== 'collapsed' })(({ theme, active, collapsed }) => ({
  borderRadius: 10,
  margin: theme.spacing(collapsed ? 0.5 : 0.5, collapsed ? 0.5 : 1.5),
  padding: theme.spacing(collapsed ? 1 : 1, collapsed ? 1 : 1.5),
  position: 'relative',
  backgroundColor: active ? 'rgba(37, 99, 235, 0.1)' : 'transparent',
  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  minHeight: 48,
  justifyContent: collapsed ? 'center' : 'flex-start',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: collapsed ? 4 : 8,
    top: 10,
    bottom: 10,
    width: 3,
    borderRadius: 4,
    backgroundColor: active ? '#2563EB' : 'transparent',
    transition: 'all 0.25s ease',
  },
  '&:hover': {
    backgroundColor: active ? 'rgba(37, 99, 235, 0.15)' : 'rgba(37, 99, 235, 0.08)',
    transform: 'translateX(2px)',
  },
  '&:active': {
    transform: 'translateX(1px)',
  },
}));

const NavItemIcon = styled(ListItemIcon, { shouldForwardProp: (prop) => prop !== 'active' && prop !== 'collapsed' })(({ theme, active, collapsed }) => ({
  minWidth: collapsed ? 'auto' : 36,
  color: active ? '#2563EB' : '#64748B',
  marginRight: collapsed ? 0 : theme.spacing(1.5),
  transition: 'all 0.25s ease',
  fontSize: collapsed ? '1.25rem' : '1.25rem',
}));

const NavItemText = styled(ListItemText, { shouldForwardProp: (prop) => prop !== 'active' && prop !== 'collapsed' })(({ theme, active, collapsed }) => ({
  opacity: collapsed ? 0 : 1,
  transition: 'opacity 0.2s ease',
  '& .MuiTypography-root': {
    fontWeight: active ? 700 : 600,
    color: active ? '#0f172a' : '#475569',
    fontSize: '0.875rem',
    letterSpacing: 0.3,
  },
}));

const CategoryLabel = styled(Typography, { shouldForwardProp: (prop) => prop !== 'collapsed' })(({ theme, collapsed }) => ({
  fontSize: '0.7rem',
  fontWeight: 700,
  color: '#94A3B8',
  textTransform: 'uppercase',
  padding: theme.spacing(collapsed ? 1.5 : 1.5, collapsed ? 0 : 2, collapsed ? 0.5 : 0.5, collapsed ? 0 : 2),
  letterSpacing: '0.8px',
  opacity: collapsed ? 0 : 1,
  transition: 'opacity 0.2s ease',
  textAlign: collapsed ? 'center' : 'left',
}));

// Logout button removed as requested

const Sidebar = ({ collapsed, onToggle, mobileOpen, onMobileClose }) => {
  const location = useLocation();
  const path = location.pathname;
  const isMobile = window.innerWidth < 768;
  
  // Check user role for access control
  const { user } = useAuth(); 
  const isAdmin = user && (user.role === 'Director' || user.role === 'admin');
  const isCommercial = user && ['commercial_manager', 'commercial_staff'].includes(user.role);
  const isSiteManager = user && (user.role === 'Site Manager' || user.role === 'site_manager');
  const isSiteEngineer = user && (user.role === 'Site Engineer' || user.role === 'site_engineer');
  const isProjectManager = user && (user.role === 'Project Manager' || user.role === 'project_manager');
  const isMaker = user && user.role === 'staff';
  const isProcurementHO = user && ['procurement_ho_manager', 'procurement_ho_staff'].includes(user.role);
  const isOperational = user && ['operational_manager', 'operational_staff'].includes(user.role);
  const isFinance = user && ['finance_manager', 'finance_staff'].includes(user.role);
  const isAccounting = user && user.role === 'accounting';
  const isGs = user && user.role === 'gs';
  const isFundRole = isMaker || isProcurementHO || isOperational || isFinance || isAccounting || isGs || isCommercial || isProjectManager || isSiteManager || isSiteEngineer;
  
  // Enhanced navigation based on role - only show implemented pages
  const getDashboardItems = () => {
    if (isMaker) {
      return [{ text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' }];
    }

    if (isAdmin) {
      return [];
    }

    const items = [{ text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' }];

    // Only show additional dashboard items for commercial (pages implemented)
    if (isCommercial) {
      items.push({ text: 'Business Analytics', icon: <AnalyticsIcon />, path: '/analytics' });
    }
    if (isProjectManager && !isAdmin) {
      items.push({ text: 'Project Overview', icon: <AssessmentIcon />, path: '/project-overview' });
    }
    // Hide additional dashboard items for other roles until pages are implemented
    
    return items;
  };

  const getProjectItems = () => {
    if (isOperational || isFinance || isAccounting || isProcurementHO || isGs) {
      return [];
    }
    if (isMaker) {
      return [];
    }

    if (isAdmin) {
      return [];
    }
    
    const items = [{ text: 'Projects', icon: <ListAltIcon />, path: '/projects' }];

    // Only show additional project items for commercial (pages implemented)
    if (isCommercial) {
      // Commercial specific items
      items.push(
        { text: 'Create Project', icon: <AddIcon />, path: '/projects/create', highlight: true },
        { text: 'Client Management', icon: <BusinessIcon />, path: '/clients' },
        { text: 'Quotations', icon: <RequestQuoteIcon />, path: '/quotations' }
      );

      // Project Manager specific items (only if commercial)
      if (isProjectManager) {
        items.push(
          { text: 'Gantt Charts', icon: <TimelineIcon />, path: '/gantt' },
          { text: 'S-Curve Analysis', icon: <ShowChartIcon />, path: '/scurve' },
          { text: 'Milestone Tracking', icon: <CalendarTodayIcon />, path: '/milestones' }
        );
      }
    }
    // Hide additional project items for other roles until pages are implemented
    
    return items;
  };

  const dashboardItems = getDashboardItems();
  const projectItems = getProjectItems();
  const mainNavItems = [...dashboardItems, ...projectItems];
  // Enhanced management navigation based on role - only show implemented pages
  const getManagementItems = () => {
    if (isMaker) {
      return [{ text: 'Expense Request', icon: <AttachMoneyIcon />, path: '/fund-requests', highlight: true }];
    }

    const items = [];
    
    // Admin and Commercial - show all implemented items
    if (isAdmin || isCommercial) {
      // Commercial Role Items
      if (isCommercial) {
        items.push(
          { text: 'Work Items Master', icon: <WorkIcon />, path: '/master-jobs' },
          { text: 'Teams', icon: <GroupIcon />, path: '/teams' },
          { text: 'Budget Planning', icon: <AttachMoneyIcon />, path: '/budget' },
          { text: 'Resource Planning', icon: <InventoryIcon />, path: '/resources' },
          { text: 'Vendor Management', icon: <BusinessIcon />, path: '/vendors' }
        );
      }

      // Project Manager Role Items (only if admin/commercial)
      if (isProjectManager) {
        items.push(
          { text: 'Approval Center', icon: <CheckCircleIcon />, path: '/progress/approval', highlight: true },
          { text: 'Team Management', icon: <GroupIcon />, path: '/team-management' },
          { text: 'Risk Management', icon: <SecurityIcon />, path: '/risk-management' },
          { text: 'Quality Control', icon: <QualityIcon />, path: '/quality-control' },
          { text: 'Cost Control', icon: <AttachMoneyIcon />, path: '/cost-control' }
        );
      }

      // Common items for admin/commercial
      items.push(
        { text: 'Documents', icon: <DescriptionIcon />, path: '/documents' }
      );

      // Admin-only: Recruitment data
      if (isAdmin) {
        items.push({ text: 'Recruitment Data', icon: <GroupIcon />, path: '/admin/recruitment', highlight: true });
      }

      // Materials for admin/commercial
      items.push({ text: 'Materials', icon: <InventoryIcon />, path: '/materials' });

      // Expense request (fund requests) for admin/commercial
      items.push({ text: 'Expense Request', icon: <AttachMoneyIcon />, path: '/fund-requests', highlight: true });
      if (isAccounting || isAdmin) {
        items.push({ text: 'Transfer Summary', icon: <AttachMoneyIcon />, path: '/fund-requests/summary' });
      }
    } else {
      // For other roles (Site Manager, Site Engineer, etc.) - only show implemented pages
      
      // Site Manager & Site Engineer - only show Update Progress (implemented)
      if (isSiteManager || isSiteEngineer) {
        items.push(
          { text: 'Create Update', icon: <UpdateIcon />, path: '/progress/update', highlight: true },
          { text: 'Update List', icon: <ListAltIcon />, path: '/progress/submissions' }
        );
        // Hide other items until pages are implemented:
        // - Safety Management, Quality Inspections, Worker Management, Equipment Management
        // - Task Management, Technical Docs, Progress Photos, Measurements
      }
      
      // Project Manager (non-admin/commercial) - only show implemented pages
      if (isProjectManager && !isAdmin && !isCommercial) {
        items.push(
          { text: 'Approval Center', icon: <CheckCircleIcon />, path: '/progress/approval', highlight: true }
        );
        // Hide other items until pages are implemented
      }

      // Fund request module for maker/approver roles
      if (isFundRole) {
        items.push({ text: 'Expense Request', icon: <AttachMoneyIcon />, path: '/fund-requests', highlight: true });
        if (isAccounting) {
          items.push({ text: 'Transfer Summary', icon: <AttachMoneyIcon />, path: '/fund-requests/summary' });
        }
      }
      
      // Hide Documents and Materials for other roles until confirmed they're needed
    }

    // Kelola Rekening (master Payee) — accounting & finance_manager (admin juga)
    if (isAdmin || isAccounting || (user && user.role === 'finance_manager')) {
      items.push({ text: 'Kelola Rekening', icon: <AccountBalanceIcon />, path: '/rekening' });
    }

    return items;
  };

  const managementNavItems = getManagementItems();

  // Enhanced reports navigation based on role - only show for admin and commercial
  const getReportItems = () => {
    const items = [];
    
    // Only show reports for admin and commercial (pages implemented)
    if (isAdmin || isCommercial) {
      // Commercial Role Reports
      if (isCommercial) {
        items.push(
          { text: 'Business Reports', icon: <AnalyticsIcon />, path: '/reports/business' },
          { text: 'Sales Analytics', icon: <BarChartIcon />, path: '/reports/sales' },
          { text: 'Profitability', icon: <AttachMoneyIcon />, path: '/reports/profitability' }
        );
      }

      // Project Manager Reports (only if admin/commercial)
      if (isProjectManager) {
        items.push(
          { text: 'Project Reports', icon: <AssessmentIcon />, path: '/reports/projects' },
          { text: 'Performance Analytics', icon: <BarChartIcon />, path: '/reports/performance' },
          { text: 'Resource Utilization', icon: <AnalyticsIcon />, path: '/reports/resources' }
        );
      }
    }
    
    // Hide reports for other roles until pages are implemented:
    // - Site Manager Reports: Daily Reports, Safety Reports, Progress Reports
    // - Site Engineer Reports: Progress Reports, Technical Reports
    
    return items;
  };

  const reportNavItems = getReportItems();

  // Settings navigation items based on role - only for commercial and admin
  const settingsNavItems = [];
  
  // Settings only for commercial and admin roles
  if (isCommercial || isAdmin) {
    settingsNavItems.push({
      text: 'Settings',
      icon: <SettingsIcon />,
      path: '/settings/profile'
    });
  }
  
  // Admin Panel - only for Director role
  if (isAdmin) {
    settingsNavItems.push({
      text: 'Admin Panel',
      icon: <AdminPanelSettingsIcon />,
      path: '/settings/admin/users',
      adminOnly: true
    });
  }

  const hasReportSection = reportNavItems.length > 0 || settingsNavItems.length > 0;

  // Remove unused function or comment it out
  // const handleLogout = () => {
  //   logout();
  //   navigate('/login');
  // };

  return (
    <Drawer
      sx={{
        width: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
          boxSizing: 'border-box',
          backgroundColor: '#FFFFFF',
          borderRight: '1px solid rgba(15, 23, 42, 0.08)',
          boxShadow: collapsed ? '4px 0 16px rgba(15, 23, 42, 0.08)' : '12px 0 24px rgba(15, 23, 42, 0.05)',
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease',
          overflowX: 'hidden',
        },
      }}
      variant={isMobile ? 'temporary' : 'permanent'}
      anchor="left"
      open={isMobile ? mobileOpen : true}
      onClose={onMobileClose}
      ModalProps={{
        keepMounted: true,
      }}
    >
      <LogoContainer collapsed={collapsed ? 1 : 0}>
        <img 
          src={logoImage} 
          alt="Hakiki Steel Logo" 
          style={{ 
            width: collapsed ? 40 : 180, 
            height: collapsed ? 40 : 50, 
            objectFit: 'contain',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }} 
        />
        {!isMobile && (
          <IconButton
            onClick={onToggle}
            sx={{
              position: 'absolute',
              right: 8,
              top: 16,
              color: '#64748B',
              backgroundColor: 'rgba(100, 116, 139, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(100, 116, 139, 0.2)',
              },
              transition: 'all 0.2s ease',
            }}
            size="small"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <MenuOpen fontSize="small" /> : <Menu fontSize="small" />}
          </IconButton>
        )}
      </LogoContainer>
      
      <Divider sx={{ backgroundColor: 'rgba(15, 23, 42, 0.06)' }} />
      
      <Box
        sx={{
          overflowY: 'auto',
          overflowX: 'hidden',
          flexGrow: 1,
          backgroundColor: '#FFFFFF',
          position: 'relative',
          height: '100%',
          pb: 2,
          '&::-webkit-scrollbar': { width: 5 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#CBD5E1', borderRadius: 6 },
          '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#94A3B8' },
        }}
      >
        <List disablePadding sx={{ pt: 1 }}>
          {/* Only render mainNavItems if user is not admin */}
          {!isAdmin && mainNavItems.map((item) => {
            const isActive = path === item.path;
            const navItem = (
              <NavItem
                component={Link}
                to={item.path}
                key={item.text}
                active={isActive ? 1 : 0}
                collapsed={collapsed ? 1 : 0}
                onClick={isMobile ? onMobileClose : undefined}
                aria-label={item.text}
                aria-current={isActive ? 'page' : undefined}
              >
                <NavItemIcon 
                  active={isActive ? 1 : 0}
                  collapsed={collapsed ? 1 : 0}
                >
                  {item.icon}
                </NavItemIcon>
                <NavItemText 
                  primary={item.text} 
                  active={isActive ? 1 : 0}
                  collapsed={collapsed ? 1 : 0}
                />
                {item.highlight && !collapsed && (
                  <Badge
                    color="primary"
                    variant="dot"
                    sx={{ ml: 'auto' }}
                  />
                )}
              </NavItem>
            );
            
            return collapsed ? (
              <Tooltip title={item.text} placement="right" key={item.text}>
                {navItem}
              </Tooltip>
            ) : navItem;
          })}
        </List>
        
        {managementNavItems.length > 0 && (
          <>
            <Divider sx={{ my: 1.5, backgroundColor: 'rgba(0, 0, 0, 0.06)' }} />
            
            <CategoryLabel collapsed={collapsed ? 1 : 0}>
              {isCommercial ? 'Business Management' :
               isProjectManager ? 'Project Control' :
               isSiteManager ? 'Site Operations' :
               isSiteEngineer ? 'Field Work' : 'Management'}
            </CategoryLabel>
            <List disablePadding>
              {managementNavItems.map((item) => {
                const isActive = path === item.path;
                const navItem = (
                  <NavItem
                    component={Link}
                    to={item.path}
                    key={item.text}
                    active={isActive ? 1 : 0}
                    collapsed={collapsed ? 1 : 0}
                    onClick={isMobile ? onMobileClose : undefined}
                    aria-label={item.text}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <NavItemIcon 
                      active={isActive ? 1 : 0}
                      collapsed={collapsed ? 1 : 0}
                    >
                      {item.icon}
                    </NavItemIcon>
                    <NavItemText 
                      primary={item.text} 
                      active={isActive ? 1 : 0}
                      collapsed={collapsed ? 1 : 0}
                    />
                    {item.highlight && !collapsed && (
                      <Badge
                        color="primary"
                        variant="dot"
                        sx={{ ml: 'auto' }}
                      />
                    )}
                  </NavItem>
                );
                
                return collapsed ? (
                  <Tooltip title={item.text} placement="right" key={item.text}>
                    {navItem}
                  </Tooltip>
                ) : navItem;
              })}
            </List>
          </>
        )}
        
        {hasReportSection && (
          <>
            <Divider sx={{ my: 1.5, backgroundColor: 'rgba(0, 0, 0, 0.06)' }} />
            
            <CategoryLabel collapsed={collapsed ? 1 : 0}>
              {isCommercial ? 'Analytics & Reports' :
              isProjectManager ? 'Performance & Reports' :
              isSiteManager ? 'Site Reports' :
              isSiteEngineer ? 'Progress & Reports' : 'Reports'}
            </CategoryLabel>
            <List disablePadding>
              {reportNavItems.map((item) => {
                const isActive = path.startsWith(item.path);
                const navItem = (
                  <NavItem
                    component={Link}
                    to={item.path}
                    key={item.text}
                    active={isActive ? 1 : 0}
                    collapsed={collapsed ? 1 : 0}
                    onClick={isMobile ? onMobileClose : undefined}
                    aria-label={item.text}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <NavItemIcon active={isActive ? 1 : 0} collapsed={collapsed ? 1 : 0}>
                      {item.icon}
                    </NavItemIcon>
                    <NavItemText 
                      primary={item.text} 
                      active={isActive ? 1 : 0}
                      collapsed={collapsed ? 1 : 0} 
                    />
                  </NavItem>
                );
                
                return collapsed ? (
                  <Tooltip title={item.text} placement="right" key={item.text}>
                    {navItem}
                  </Tooltip>
                ) : navItem;
              })}
              
              {/* Settings - only show for commercial and admin roles */}
              {settingsNavItems.length > 0 && settingsNavItems.map((item) => {
                const isActive = path.startsWith('/settings');
                const navItem = (
                  <NavItem
                    component={Link}
                    to={item.path}
                    key={item.text}
                    active={isActive ? 1 : 0}
                    collapsed={collapsed ? 1 : 0}
                    onClick={isMobile ? onMobileClose : undefined}
                    aria-label={item.text}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <NavItemIcon active={isActive ? 1 : 0} collapsed={collapsed ? 1 : 0}>
                      {item.icon}
                    </NavItemIcon>
                    <NavItemText 
                      primary={item.text} 
                      active={isActive ? 1 : 0}
                      collapsed={collapsed ? 1 : 0} 
                    />
                    {item.adminOnly && !collapsed && (
                      <Badge
                        color="secondary"
                        variant="dot"
                        sx={{ ml: 'auto' }}
                      />
                    )}
                  </NavItem>
                );
                
                return collapsed ? (
                  <Tooltip title={item.text} placement="right" key={item.text}>
                    {navItem}
                  </Tooltip>
                ) : navItem;
              })}
            </List>
          </>
        )}
        
        {/* Logout button removed as requested */}
      </Box>
    </Drawer>
  );
};

export default Sidebar;
